// Glob matching for extension selectors.
//
// Deliberately tiny: `*`, `?` and a leading `!` for negation is everything the
// selectors need, and it keeps the extension system dependency-free.

const cache: { [ pattern: string ]: RegExp } = {}

function toRegExp(pattern: string) {
	if (!cache[pattern]) {
		const source = pattern
			// escape every regexp special except * and ?, which we translate below
			.replace(/[.+^${}()|[\]\\]/g, '\\$&')
			.replace(/\*/g, '.*')
			.replace(/\?/g, '.')
		cache[pattern] = new RegExp('^' + source + '$')
	}
	return cache[pattern]
}

/** a single pattern against a single value. `!foo` matches everything but `foo`. */
export function globMatch(pattern: string, value: string | null | undefined): boolean {
	if (pattern.startsWith('!')) return !globMatch(pattern.slice(1), value)
	if (value == null) return false
	return toRegExp(pattern).test(value)
}

/**
 * A selector filter list against a value.
 *
 * Positive patterns are ORed - any one of them matching is enough. Negative
 * patterns are ANDed as exclusions, so `[ '!master' ]` reads as "every
 * environment except master" and `[ 'world*', '!world-test' ]` as "the world
 * environments, but not the test one".
 *
 * An empty list is treated as no constraint (the registry warns about it),
 * because a list that can never match is always a mistake rather than an intent.
 */
export function globMatchList(patterns: string[], value: string | null | undefined): boolean {
	if (!patterns.length) return true

	const positive = patterns.filter(p => !p.startsWith('!'))
	const negative = patterns.filter(p => p.startsWith('!'))

	if (!negative.every(p => globMatch(p, value))) return false
	if (!positive.length) return true
	return positive.some(p => globMatch(p, value))
}
